import { motion } from 'framer-motion'
import { Brain, Plus, Settings, Key, CheckCircle } from 'lucide-react'

const ModelConfig = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prose prose-invert max-w-none"
    >
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <Brain className="mr-3 text-accent-cyan" size={32} />
        配置模型
      </h1>

      <p className="text-gray-400 mb-8">
        Avelia 支持多种 AI 模型提供商，包括平台默认模型和自定义模型。你可以根据需求灵活配置。
      </p>

      {/* 平台模型 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">使用平台模型</h2>
        <div className="glass-effect rounded-lg p-5 border-l-4 border-green-500">
          <h3 className="text-lg font-semibold mb-3 flex items-center">
            <CheckCircle className="mr-2 text-green-400" size={24} />
            开箱即用
          </h3>
          <p className="text-gray-400 mb-4">
            Avelia 默认提供经过优化的平台模型，无需配置 API 密钥即可使用：
          </p>
          <ul className="space-y-2 text-sm text-gray-400">
            <li className="flex items-start">
              <span className="text-accent-cyan mr-2">✓</span>
              <span><strong className="text-white">智能选择：</strong>根据任务类型自动选择最合适的模型</span>
            </li>
            <li className="flex items-start">
              <span className="text-accent-cyan mr-2">✓</span>
              <span><strong className="text-white">性能优化：</strong>经过调优，响应速度快，效果稳定</span>
            </li>
            <li className="flex items-start">
              <span className="text-accent-cyan mr-2">✓</span>
              <span><strong className="text-white">统一计费：</strong>无需管理多个 API 密钥，使用更便捷</span>
            </li>
          </ul>
          <div className="mt-4 p-3 bg-green-500/10 rounded text-sm">
            <p className="text-green-300">
              💡 <strong>提示：</strong>平台模型已经过优化配置，推荐新用户直接使用。
            </p>
          </div>
        </div>
      </section>

      {/* 添加自定义模型 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">添加自定义模型</h2>
        <p className="text-gray-400 mb-4">
          如果你有自己的 API 密钥或想使用本地模型，可以自定义配置：
        </p>

        <div className="space-y-6">
          {/* OpenAI */}
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mr-3">
                <span className="text-sm font-bold">AI</span>
              </div>
              配置 OpenAI 模型
            </h3>
            
            <ol className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-purple/20 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                <span>打开设置 → 模型配置 → 点击"添加模型"</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-purple/20 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                <span>选择 <strong className="text-white">OpenAI</strong> 作为提供商</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-purple/20 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                <div>
                  <span>填写配置信息：</span>
                  <div className="mt-2 p-3 bg-black/30 rounded font-mono text-xs">
                    <div className="mb-1"><span className="text-accent-cyan">API Key:</span> sk-xxxxxxxxxxxxxxxx</div>
                    <div className="mb-1"><span className="text-accent-cyan">Base URL:</span> https://api.openai.com/v1</div>
                    <div><span className="text-accent-cyan">Model:</span> gpt-4o</div>
                  </div>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-purple/20 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                <span>点击"测试连接"验证配置，然后保存</span>
              </li>
            </ol>
          </div>

          {/* Ollama */}
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center mr-3">
                <span className="text-sm font-bold">🦙</span>
              </div>
              配置 Ollama 本地模型
            </h3>
            
            <ol className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-purple/20 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                <div>
                  <span>确保 Ollama 已安装并运行</span>
                  <div className="mt-1 text-xs text-gray-500">
                    下载地址：<a href="https://ollama.ai" target="_blank" rel="noopener noreferrer" className="text-accent-cyan hover:underline">ollama.ai</a>
                  </div>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-purple/20 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                <div>
                  <span>下载模型（在终端中运行）：</span>
                  <div className="mt-2 p-3 bg-black/30 rounded font-mono text-xs">
                    ollama pull llama3.1
                  </div>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-purple/20 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                <div>
                  <span>在 Avelia 中添加 Ollama 模型：</span>
                  <div className="mt-2 p-3 bg-black/30 rounded font-mono text-xs">
                    <div className="mb-1"><span className="text-accent-cyan">Base URL:</span> http://localhost:11434</div>
                    <div><span className="text-accent-cyan">Model:</span> llama3.1</div>
                  </div>
                </div>
              </li>
            </ol>

            <div className="mt-4 p-3 bg-blue-500/10 rounded text-sm border border-blue-500/30">
              <p className="text-blue-300">
                <strong>优势：</strong>完全本地运行，数据不会发送到云端，隐私性最高。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 模型管理 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">模型管理</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
          <div className="glass-effect rounded-lg p-4">
            <h3 className="font-semibold mb-2 flex items-center text-sm">
              <Settings className="mr-2 text-accent-cyan" size={18} />
              切换模型
            </h3>
            <p className="text-xs text-gray-400">
              在对话界面右上角可以快速切换当前使用的模型，每个会话可以独立设置模型。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-4">
            <h3 className="font-semibold mb-2 flex items-center text-sm">
              <Key className="mr-2 text-accent-cyan" size={18} />
              编辑配置
            </h3>
            <p className="text-xs text-gray-400">
              在设置中可以随时修改模型配置、更新 API 密钥、调整参数等。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-4">
            <h3 className="font-semibold mb-2 flex items-center text-sm">
              <Plus className="mr-2 text-accent-cyan" size={18} />
              添加多个模型
            </h3>
            <p className="text-xs text-gray-400">
              可以同时配置多个模型，根据不同任务选择最合适的模型使用。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-4">
            <h3 className="font-semibold mb-2 flex items-center text-sm">
              <CheckCircle className="mr-2 text-accent-cyan" size={18} />
              测试连接
            </h3>
            <p className="text-xs text-gray-400">
              配置后可以测试连接状态，确保模型可用后再开始使用。
            </p>
          </div>
        </div>
      </section>

      {/* 常见问题 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">常见问题</h2>
        
        <div className="space-y-4">
          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between text-sm">
              如何获取 OpenAI API Key？
              <Plus className="text-accent-cyan group-open:rotate-45 transition-transform" size={18} />
            </summary>
            <div className="mt-3 text-xs text-gray-400 pl-4 border-l-2 border-accent-cyan/30">
              <ol className="list-decimal ml-4 space-y-2">
                <li>访问 <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer" className="text-accent-cyan hover:underline">platform.openai.com</a></li>
                <li>注册并登录账号</li>
                <li>进入 API Keys 页面</li>
                <li>点击"Create new secret key"生成密钥</li>
                <li>复制密钥并妥善保存（只显示一次）</li>
              </ol>
            </div>
          </details>

          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between text-sm">
              平台模型和自定义模型有什么区别？
              <Plus className="text-accent-cyan group-open:rotate-45 transition-transform" size={18} />
            </summary>
            <div className="mt-3 text-xs text-gray-400 pl-4 border-l-2 border-accent-cyan/30">
              <ul className="list-disc ml-4 space-y-2">
                <li><strong className="text-white">平台模型：</strong>开箱即用，无需配置，统一计费</li>
                <li><strong className="text-white">自定义模型：</strong>使用自己的 API Key，独立计费，更灵活</li>
                <li>两者可以共存，根据需求选择使用</li>
              </ul>
            </div>
          </details>

          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between text-sm">
              如何选择合适的模型？
              <Plus className="text-accent-cyan group-open:rotate-45 transition-transform" size={18} />
            </summary>
            <div className="mt-3 text-xs text-gray-400 pl-4 border-l-2 border-accent-cyan/30">
              <ul className="list-disc ml-4 space-y-2">
                <li><strong className="text-white">通用任务：</strong>GPT-4o、Claude</li>
                <li><strong className="text-white">代码相关：</strong>Claude、DeepSeek-Coder</li>
                <li><strong className="text-white">隐私要求高：</strong>Ollama 本地模型</li>
                <li><strong className="text-white">成本敏感：</strong>DeepSeek、本地模型</li>
              </ul>
            </div>
          </details>
        </div>
      </section>
    </motion.div>
  )
}

export default ModelConfig
